import React, { useEffect, useState } from 'react';
import { Select, Option, CircularProgress } from '@mui/joy';
import { directSupabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';

type Supplier = Database['public']['Tables']['suppliers']['Row'];

interface SupplierSelectorProps {
  value: string;
  onChange: (id: string) => void;
  supplierType?: 'Glass' | 'Windows';
}

const SupplierSelector: React.FC<SupplierSelectorProps> = ({ 
  value, 
  onChange,
  supplierType 
}) => {
  const [suppliersList, setSuppliersList] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        setLoading(true);
        const { data, error } = await directSupabase
          .from('suppliers')
          .select('*')
          .order('supplier_name', { ascending: true });
        
        if (error) {
          throw new Error(error.message);
        }
        
        // Filter suppliers by category if specified
        const filteredSuppliers = supplierType 
          ? data?.filter((s: any) => s.category === supplierType) || []
          : data || [];
        setSuppliersList(filteredSuppliers);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load suppliers');
        console.error('Error loading suppliers:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSuppliers();
  }, [supplierType]);

  if (loading) {
    return <CircularProgress size="sm" />;
  }

  if (error) {
    console.error('SupplierSelector error:', error);
    return null;
  }

  return (
    <Select
      value={value}
      onChange={(_, val) => val && onChange(val)}
      sx={{ 
        minWidth: { xs: '100%', sm: 220 },
        width: { xs: '100%', sm: 'auto' }
      }}
      placeholder="Select supplier"
    >
      {suppliersList.map(supplier => (
        <Option key={supplier.id} value={supplier.id}>
          {supplier.supplier_name}
        </Option>
      ))}
    </Select>
  );
};

export default SupplierSelector; 